import React, {Suspense, useEffect, useRef} from 'react';
import { Spin } from 'antd';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import get from 'lodash.get';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorComponent from '@/common/error-boundary-fallback';
import { boundaryErrorHandler } from '@/common/telemetry';
import DefaultSkeleton from '@/common/skeleton/default-skeleton';
import { ADMINISTRATOR } from '@/constants/roles';
import { setAllTableSiteId } from '@/pages/shared/shared.reducer';
import { dashboardApi } from '@/pages/dashboard/dashboard.reducer';
import { getDefaultDashboardRoute } from '@/constants/default-dashboard-routes';

const handleRouteError = boundaryErrorHandler('private-route');
const NO_PERMISSIONS = [];

const PrivateRoute = ({ component: Component, ...rest }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const hasInitializedDashboardSettingsRefetch = useRef(false);

    const isLogin = useSelector(({ userDetails }) => get(userDetails, 'isLogin'));
    const maxUserRole = useSelector(({ userDetails }) => userDetails.maxUserRole);
    const defaultDashboardId = useSelector(({ userDetails }) => get(userDetails, 'setting.defaultDashboardId'));
    const permissions = useSelector(({ userDetails }) => userDetails.permissions || NO_PERMISSIONS);
    const setting = useSelector(({ userDetails }) => userDetails.setting || null);
    const haveAccessToAllSitesDashboard = useSelector(({ shared }) => shared.accessToAllSitesDashboard);
    const currentSiteId = useSelector(({ shared }) => get(shared, 'siteId'));
    const siteSelectionInitialized = useSelector(({ shared }) => get(shared, 'siteSelectionInitialized'));
    const defaultSiteId = useSelector(({ userDetails }) => get(userDetails, 'setting.defaultSiteId'));

    // Previously supplied by connect's mapState and forwarded to the routed
    // component through the props spread below; several pages still read them.
    const stateProps = {
        isLogin,
        maxUserRole,
        defaultDashboardId,
        permissions,
        setting,
        haveAccessToAllSitesDashboard,
        currentSiteId,
        siteSelectionInitialized,
        defaultSiteId
    };

    const isLoggedIn = isLogin;
    const sites = useSelector(state => state.dashboardPage.sites)
    const hasCurrentSite = sites.some(site => String(site.siteId) === String(currentSiteId));
    const hasAllSitesSelection = String(currentSiteId) === '0';
    const configuredDefaultSiteId = defaultSiteId || sites[0]?.siteId;
    const fallbackSiteId = sites.some(site => String(site.siteId) === String(configuredDefaultSiteId))
        ? configuredDefaultSiteId
        : sites[0]?.siteId;
    const shouldRestoreUninitializedSite = !siteSelectionInitialized
        && (currentSiteId == null || String(currentSiteId) === '0');
    const shouldRestoreInvalidSite = currentSiteId != null
        && currentSiteId !== ''
        && !hasAllSitesSelection
        && !hasCurrentSite;
    const shouldRestoreSite = sites.length > 0
        && fallbackSiteId != null
        && (shouldRestoreUninitializedSite || shouldRestoreInvalidSite);

    useEffect(() => {
        if (shouldRestoreSite && String(fallbackSiteId) !== String(currentSiteId)) {
            dispatch(setAllTableSiteId(fallbackSiteId));
        }
    }, [dispatch, fallbackSiteId, currentSiteId, shouldRestoreSite]);


    const defaultDashboardLink = getDefaultDashboardRoute(defaultDashboardId);

    const highestRoleId = maxUserRole;
    const isAdmin = highestRoleId === ADMINISTRATOR;

    const allLoaded =
        permissions.length > 0 &&
       sites.length > 0 &&
        setting &&
        ((haveAccessToAllSitesDashboard &&
            rest.isAllSiteDashboard &&
            permissions.length > 1) ||
            !rest.isAllSiteDashboard);

    useEffect(() => {
        if (!isLoggedIn || !allLoaded || rest.loginPage) {
            return;
        }

        if (!hasInitializedDashboardSettingsRefetch.current) {
            hasInitializedDashboardSettingsRefetch.current = true;
            return;
        }

        dispatch(
            dashboardApi.endpoints.fetchAccountDashboardSettings.initiate(undefined, {
                subscribe: false,
                forceRefetch: true,
            })
        );
    }, [allLoaded, dispatch, isLoggedIn, location.pathname, rest.loginPage]);

    if (!isLoggedIn && !rest.loginPage) {
        return <Navigate to={{ pathname: '/login', state: { from: location } }} />;
    }

    if (!allLoaded && !rest.loginPage) {
        return (
            <Spin percent='auto' fullscreen />
        );
    }

    if (!rest.loginPage && shouldRestoreSite && String(fallbackSiteId) !== String(currentSiteId)) {
        return (
            <Spin percent='auto' fullscreen />
        );
    }

    if (rest.requiredSetting && setting?.[rest.requiredSetting] !== true) {
        return <Navigate to={{ pathname: defaultDashboardLink }} />;
    }

    if (!isAdmin) {
        const isForbidden =
            highestRoleId != null &&
            Array.isArray(rest.forbiddenRoles) &&
            rest.forbiddenRoles.includes(highestRoleId);

        if (isForbidden) {
            return <Navigate to={{ pathname: defaultDashboardLink }} />;
        }
    }

    return (
        <Suspense fallback={rest.skeleton || <DefaultSkeleton />}>
            <ErrorBoundary FallbackComponent={ErrorComponent}
                onError={handleRouteError}
                onReset={() => navigate('/')}
            >
                <Component {...rest} {...stateProps} />
            </ErrorBoundary>
        </Suspense>
    );
};

export default PrivateRoute;
